import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { MatDialog } from '@angular/material/dialog';
import { CopyDialogComponent } from '@library/common';

import { Subject } from 'rxjs';
import {
   delay,
   filter,
   switchMap,
   take,
   takeUntil,
   tap,
} from 'rxjs/operators';

import {
   BreadcrumbService,
   BreakpointService,
   SearchService,
   Preferences,
} from '@library/core/services';
import { AccountService } from '@library/modules/user';
import { ReportDetailsService } from '../report.details.service';
import { ReportService } from './report.service';

import * as moment from 'moment';

@Component({
   selector: 'lib-reports-report',
   templateUrl: './report.component.html',
   styleUrls: ['./report.component.scss'],
   providers: [ ReportService ],
})
export class ReportComponent implements OnInit, OnDestroy {
   dataSource: MatTableDataSource<any[]> = new MatTableDataSource();

   activeAccountNumber: number;

   hovered: string;

   @ViewChild(MatSort) sort!: MatSort;
   _sort = false;

   @ViewChild(MatPaginator) paginator: MatPaginator;
   pageIndex = 0;
   get pageSize() { return this.prefs.getPageSize( 'report' ); }
   set pageSize( value ) { this.prefs.setPageSize( 'report', value ); }

   _onDestroy: Subject<void> = new Subject();

   constructor(
      private reportDetails: ReportDetailsService,
      private route: ActivatedRoute,
      private router: Router,
      private accountService: AccountService,
      private search: SearchService,
      private breadcrumbs: BreadcrumbService,
      private dialog: MatDialog,
      private prefs: Preferences,

      public breakpoints: BreakpointService,
      public report: ReportService,
   ) { }

   ngOnInit() {
      this.breadcrumbs.newCrumb({ label: 'Reports', path: '/report' });
      this.breadcrumbs.addCrumble( 'run' );

      this.search.showSearch = true;
      this.search.params.get( 'text' ).valueChanges.subscribe( value => this.dataSource.filter = value );

      this.accountService.active.pipe( take( 1 ))
         .subscribe( a => this.activeAccountNumber = a.accountNumber );

      if ( !this.route.snapshot.params.id && !this.reportDetails.report$.getValue() ) {
         this.router.navigate([ 'report' ]);

         return;
      }

      this.loadReport();
   }

   loadReport(refresh = false){
      this.report.reset();      

      this.report.response$.pipe(
         tap( _ => this.dataSource.data = this.report.response.collection ),
         delay( 1 ),
         takeUntil( this._onDestroy ),
      ).subscribe( _ => {
         this.setSort();
         this.setPagination();
      });

      this.route.params.pipe(
         filter( params => !!params.id ),
         // found an id -- load the report from the backend
         switchMap( params => refresh ? this.reportDetails.refresh( params.id ).pipe( take( 1 )) : this.reportDetails.load( params.id ).pipe( take( 1 ))),
         takeUntil( this._onDestroy ),
      ).subscribe(
         _ => this.report.load().pipe( take( 1 )).subscribe(),
         _ => this.report.pagination.stop(),
      );

      this.route.params.pipe(
         filter( params => !params.id ),
         takeUntil( this._onDestroy ),
      ).subscribe( _ => this.report.load().pipe( take( 1 )).subscribe() );
   }

   ngOnDestroy() {
      this._onDestroy.next();
      this._onDestroy.complete();

      this.report.reset();
   }

   setSortDir( column: string ): void {
      const sortable = ({ id: column, start: undefined, disableClear: undefined }) as MatSortable;
      this.sort?.sort( sortable );
   }

   setSort() {
      if ( !this.sort || !this.report?.report?.schema ) { return; }

      // set sort dir for primary column
      this.report.report.schema.filter( c => c.sort_ord ).map( c => {
         this.sort?.sort(({ id: c.name, start: undefined, disableClear: undefined }) as MatSortable );
      });

      this.dataSource.sort = this.sort;

      // set custom sorting logic
      this.dataSource.sortingDataAccessor = ( d: any, sortHeaderId: string ): string => {
         if ( typeof d[sortHeaderId] === 'string' ) {
            // sort by case insensitive
            return d[sortHeaderId].toLowerCase();
         }

         return d[sortHeaderId];
      };
   }

   setPagination() {
      if ( !this.paginator ) { return; }

      this.dataSource.paginator = this.paginator;

      // If user changes the sort order, reset back to the first page
      if ( this.sort ) { this.sort.sortChange.subscribe( () => this.paginator.firstPage() ); }
   }

   stickyHeader(): boolean {
      const header = document.getElementById( 'dataTable' );

      return header.getBoundingClientRect().top < 0;
   }

   getDate() {
      return moment().format( 'YYYY-MM-DD' );
   }

   viewData( element: any, column:any  ) {
      let message = element[column];

      if (message) {
         this.dialog.open( CopyDialogComponent, { data: { message,column } });
      }
   }

   downloadCSV( filteredData: any ) {
      this.report.downloadCsv(filteredData);
   }

   downloadJSON( filteredData: any ) {
      this.report.downloadJson(filteredData);
   }

   downloadTemplate( data: any ) {
      this.report.download(data);
   }
}
